<script setup lang="ts">
    import { onMounted, ref, watch } from "vue";
    import { CmnhDatepicker } from "@cmnhospitals/components-vue/datepicker";
    import { CmnhInput } from "@cmnhospitals/components-vue";
    import { DateTime } from "luxon";
    import { TransactionStatus, PaymentMethodType, SearchFilters } from "@/typings";
    import { getDefaultSearchFilters } from "@/utils";
    import { applyInitialFilters, clearedFilterKeys } from "@/composables/search-utils";

    const props = defineProps<{
        transactionStatuses: TransactionStatus[];
        paymentMethodTypes: PaymentMethodType[];
        /** Comma-separated office numbers from the office selector; the form never edits these. */
        initialLocationNumbers: string;
        initialSearchFilters: SearchFilters;
    }>();

    const emit = defineEmits(["update-search", "reset-search"]);

    function emitSearch() {
        emit("update-search", {
            ...searchFilters.value,
            locationNumbers: props.initialLocationNumbers,
        });
    }


    const today = DateTime.now().toISO(); // Ensure today is an ISO 8601 string
    const startDatepickerKey = ref(0);
    const endDatepickerKey = ref(0);

    // Restored filters are applied last so a value carried in from the URL wins over
    // the first-option dropdown defaults.
    const searchFilters = ref<SearchFilters>(applyInitialFilters({
        ...getDefaultSearchFilters(),
        paymentMethodTypeId: String(props.paymentMethodTypes?.[0]?.value ?? ""),
        transactionStatus: String(props.transactionStatuses?.[0]?.value ?? ""),
    }, props.initialSearchFilters));


    /**
     * Converts a JS Date into an ISO 8601 string format.
     * Example output: "2025-02-10T17:20:32.42"
     */
    function formatLuxonDate(dateVal: Date, timeOfDay: "start" | "end" = "start"): string | undefined {
        const dt = DateTime.fromJSDate(dateVal);
        const formattedDate = timeOfDay === "start"
            ? dt.startOf("day").toISO({ includeOffset: false })  // 00:00:00 (No Offset)
            : dt.endOf("day").toISO({ includeOffset: false });   // 23:59:59.999 (No Offset)

        return formattedDate ?? undefined;
    }

    const updateStartDate = (date?: Date | Date[]) => {
        if (!date) {
            searchFilters.value.startDate = undefined;
            return;
        }

        const jsDate = Array.isArray(date) ? date[0] : date;
        const formattedDate = jsDate ? formatLuxonDate(jsDate, "start") : undefined; // ⏰ Set to 00:00:00

        searchFilters.value.startDate = formattedDate;

        // If a "From" date is selected and no "To" date, set the "To" date to today at 11:59 PM
        if (formattedDate && !searchFilters.value.endDate) {
            searchFilters.value.endDate = formatLuxonDate(new Date(), "end");
        }
    };

    const updateEndDate = (date?: Date | Date[]) => {
        if (!date) {
            searchFilters.value.endDate = undefined;
            return;
        }
        const jsDate = Array.isArray(date) ? date[0] : date;
        const formattedDate = jsDate ? formatLuxonDate(jsDate, "end") : undefined; // ⏰ Set to 23:59:59

        if (formattedDate) {
            searchFilters.value.endDate = formattedDate;

            // If a "To" date is selected and no "From" date, set the "From" date to the same day at 00:00:00
            if (!searchFilters.value.startDate) {
                searchFilters.value.startDate = formatLuxonDate(jsDate, "start");
            }
        }
    };

    // Clear all filters, resetting them to undefined
    const datepickerKey = ref(0); // This forces the CmnhDatepicker component to reload

    function clearFilters() {
        searchFilters.value = {
            ...getDefaultSearchFilters(),
            locationNumbers: props.initialLocationNumbers ?? "",
            startDate: undefined,
            endDate: undefined,
            paymentMethodTypeId: String(props.paymentMethodTypes?.[0]?.value ?? ""),
            transactionStatus: String(props.transactionStatuses?.[0]?.value ?? ""),
        };

        datepickerKey.value++;

        setTimeout(() => {
            document.querySelectorAll(".air-datepicker__input").forEach(input => {
                (input as HTMLInputElement).value = "";
            });
        }, 50);

        // The all-empty key map lets the caller drop every filter param from the URL.
        emit("reset-search", clearedFilterKeys(getDefaultSearchFilters()));
    }


    function onSearch() {
        const hasFilters = Object.values(searchFilters.value).some(
            val => val !== undefined && val !== null && val !== ''
        );

        if (hasFilters) {
            emitSearch();
        } else {
            emit("reset-search", clearedFilterKeys(getDefaultSearchFilters()));
        }
    }


    watch(() => searchFilters.value.startDate, () => {
        endDatepickerKey.value++; // Force re-render To Date when From Date changes
    });

    watch(() => searchFilters.value.endDate, () => {
        startDatepickerKey.value++; // Force re-render From Date when To Date changes
    });

    watch(
        () => props.initialSearchFilters,
        val => {
            if (val) searchFilters.value = { ...getDefaultSearchFilters(), ...val };
        }
    );

    // Listen for Enter key on any input and trigger search
    onMounted(() => {
        const inputEls = document.querySelectorAll<HTMLInputElement>(".search-form input");

        inputEls.forEach(input => {
            input.addEventListener("keydown", (e) => {
                if (e.key === "Enter") {
                    e.preventDefault(); // prevent default to avoid clearing input
                    emitSearch();
                }
            });
        });
    });

</script>

<template>
    <form @submit.prevent="onSearch">
        <div class="search-form">
            <h2>Refine Search Criteria</h2>
            <div class="form-group">
                <CmnhInput type="search" label="Payment #" placeholder="Payment #" :hide-label="true" v-model="searchFilters.transactionNumber" />
                <CmnhInput type="search" label="Payment Amount" placeholder="Payment Amount" :hide-label="true" v-model="searchFilters.transactionAmount" />
                <CmnhInput type="search" label="Associate Name" placeholder="Associate Name" :hide-label="true" v-model="searchFilters.userName" />
                <CmnhInput type="search" label="Associate ID" placeholder="Associate ID" :hide-label="true" v-model="searchFilters.employeeNumber" />
            </div>

            <div class="form-group">
                <CmnhInput type="search" label="Transaction ID" placeholder="Transaction ID" :hide-label="true" v-model="searchFilters.processorTransactionId" />
                <CmnhInput type="search" label="Check Number" placeholder="Check Number" :hide-label="true" v-model="searchFilters.checkNumber" />
                <CmnhInput type="search" label="Order Number" placeholder="Order Number" :hide-label="true" v-model="searchFilters.orderNumber" />
            </div>

            <div class="form-group">
                <select v-model="searchFilters.paymentMethodTypeId" class="form-control">
                    <option v-for="type in props.paymentMethodTypes" :key="type.value" :value="type.value">
                        {{ type.text }}
                    </option>
                </select>

                <select v-model="searchFilters.transactionStatus" class="form-control">
                    <option v-for="status in props.transactionStatuses" :key="status.value" :value="status.value">
                        {{ status.text }}
                    </option>
                </select>
                <div class="search-form--dates">
                    <div>
                        <CmnhDatepicker :key="startDatepickerKey" label="From" :readonly-input="true" :hide-label="true" :hideInitialValue="true" @date-selected="updateStartDate" :selectedDate="searchFilters.startDate || undefined" :max-date="searchFilters.endDate || today" :auto-close="true" placeholder="From Date" />
                    </div>
                    <div>
                        <CmnhDatepicker :key="endDatepickerKey" label="To" :readonly-input="true" :hide-label="true" :hideInitialValue="true" @date-selected="updateEndDate" :selectedDate="searchFilters.endDate || undefined" :max-date="today" :min-date="searchFilters.startDate" :auto-close="true" placeholder="To Date" />
                    </div>
                </div>

                <div class="search-form--dates-clear">
                    <button type="button" data-qa-id="payment-history-clear-dates-button" @click="searchFilters.endDate = undefined; searchFilters.startDate = undefined;" class="btn btn-default btn-xs">Clear Dates</button>
                </div>
            </div>

            <div class="form-group action-btns-container">
                <button type="button" data-qa-id="payment-history-clear-filters-button" @click="clearFilters" class="btn btn-default">Clear</button>
                <button type="submit" data-qa-id="payment-history-search-button" class="btn btn-primary">Search</button>
            </div>
        </div>
    </form>
</template>

<style scoped lang="scss">
@import "../../../Styles/imports";

.search-form {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: $indent-default;
    background-color: $cmn-gray-light;
    padding: 0 $indent-default;

    h2 {
        color: $cmn-dark-gray;
        font-weight: 600;
        margin: $indent-default 0 0;
        font-size: 14px;
        width: 100%;
    }

    .search-form--dates {
        display: flex;
        gap: $indent-default;
        align-items: center;

        & > div {
            width: 50%;

            @media (max-width: $screen-sm-max) {
                width: 100%;
            }
        }
    }

    .search-form--dates-clear {
        display: flex;
        justify-content: flex-end;
    }

    // Datepicker
    .air-datepicker__input {
        border-radius: 0;
        font-size: 11px;
        font-weight: 600;
        padding-left: 12px;
        padding: 6px 12px;
        line-height: 1.53;
    }

    .air-datepicker-button {
        color: $white;
        background-color: $cmn-dark-gray;
        border-color: $cmn-dark-gray;
    }

    .air-datepicker__container {
        .air-datepicker {
            min-width: 250px;
            font-size: 11px;
        }

        .air-datepicker-nav {
            display: flex;
            align-items: center;
            min-height: 50px;

            .air-datepicker-nav--action {
                svg {
                    path {
                        fill: #aaaaaa;
                    }
                }
            }
        }

        .air-datepicker-cell.-current-,
        .air-datepicker-nav--title i {
            color: $cmn-dark-gray;
            font-weight: bold;
        }

        .air-datepicker-cell.-focus-,
        .air-datepicker-cell.-selected- {
            background-color: $cmn-dark-gray;
            color: #fff;
        }
    }

    select.form-control {
        padding-inline-start: 8px;
    }

    .form-group {
        margin-bottom: 0;
    }

    .form-group:not(.action-btns-container) {
        width: 32%;
        display: flex;
        flex-direction: column;
        gap: $indent-default;

        @media (max-width: $screen-sm-max) {
            width: 48%;
            margin-bottom: 0;
        }

        @media (max-width: $screen-xs-max) {
            width: 100%;
            margin-bottom: 0;
        }
    }

    /* Center the Clear button */
    .action-btns-container {
        display: flex;
        justify-content: flex-end;
        margin-bottom: $indent-default;
        width: 100%;

        button:first-child {
            margin-right: $indent-default;
        }
    }
}
</style>
